/* Pokémon 30th Celebration segment (LSC) — flow-field canvas, shelf controls,
   countdown, peek dialog, entrance + hover motion.
   House rules:
   - GSAP/ScrollTrigger come from theme.liquid (never loaded twice); everything
     except the entrance/hover works with no GSAP at all.
   - Entrance is skipped (content stays visible) under reduced motion, in the
     theme editor, when GSAP is missing, or when the tab loads hidden; anything
     armed hidden has two independent backstops so the shelf can never ship blank.
   - The flow field is a transparent 2D canvas (no WebGL/WebGPU): paused when the
     section is off-screen or the tab is hidden, DPR capped, self-throttling on
     slow frames, and rendered ONCE as a still under reduced motion. */
(function () {
  'use strict';

  /* ======================================================================
     Flow field — classic 2D gradient noise, seeded, small.
     ====================================================================== */
  function makeNoise(seed) {
    var p = new Uint8Array(512);
    var perm = [];
    for (var i = 0; i < 256; i++) perm[i] = i;
    var s = seed >>> 0 || 1;
    function rnd() { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) % 10000) / 10000; }
    for (var j = 255; j > 0; j--) {
      var k = Math.floor(rnd() * (j + 1));
      var t = perm[j]; perm[j] = perm[k]; perm[k] = t;
    }
    for (var n = 0; n < 512; n++) p[n] = perm[n & 255];
    var GX = [1, -1, 1, -1, 1, -1, 0, 0], GY = [1, 1, -1, -1, 0, 0, 1, -1];
    function fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }
    function lerp(a, b, t) { return a + t * (b - a); }
    function grad(h, x, y) { h &= 7; return GX[h] * x + GY[h] * y; }
    return function (x, y) {
      var X = Math.floor(x) & 255, Y = Math.floor(y) & 255;
      x -= Math.floor(x); y -= Math.floor(y);
      var u = fade(x), v = fade(y);
      var A = p[X] + Y, B = p[X + 1] + Y;
      return lerp(
        lerp(grad(p[A], x, y), grad(p[B], x - 1, y), u),
        lerp(grad(p[A + 1], x, y - 1), grad(p[B + 1], x - 1, y - 1), u),
        v
      );
    };
  }

  function initFlow(root) {
    var canvas = root.querySelector('[data-pk30-flow-canvas]');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var intensity = Math.max(1, Math.min(3, parseInt(root.dataset.pk30Flow, 10) || 2));
    var noise = makeNoise(301996);
    var PALETTE = [
      [232, 194, 104], [232, 194, 104], [245, 217, 138], /* gold family */
      [255, 255, 255], [255, 255, 255],                  /* white */
      [214, 160, 70]                                     /* deep gold */
    ];

    var W = 0, H = 0, dpr = 1, parts = [], count = 0, raf = 0, running = false;
    var visible = false, pageVisible = document.visibilityState === 'visible';
    var t0 = performance.now(), last = t0, slowFrames = 0, budget = 1;
    var pointer = { x: -1e4, y: -1e4, active: false };
    var SCALE = 0.0022, SPEED = 0.55 + intensity * 0.25, DRIFT = 0.00012;

    function resize() {
      var r = root.getBoundingClientRect();
      var w = Math.max(1, Math.round(r.width)), h = Math.max(1, Math.round(r.height));
      dpr = Math.min(window.devicePixelRatio || 1, 1.25);
      if (w === W && h === H) return;
      W = w; H = h;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      seed();
    }
    function seed() {
      var area = W * H;
      count = Math.round(Math.min(520, Math.max(110, area / 7800)) * (0.7 + intensity * 0.25) * budget);
      parts = [];
      for (var i = 0; i < count; i++) parts.push(spawn({}));
    }
    function spawn(o) {
      o.x = Math.random() * W; o.y = Math.random() * H;
      o.px = o.x; o.py = o.y;
      o.life = 60 + Math.random() * 220;
      o.age = Math.random() * o.life;
      var c = PALETTE[(Math.random() * PALETTE.length) | 0];
      o.r = c[0]; o.g = c[1]; o.b = c[2];
      o.a = 0.16 + Math.random() * 0.34;
      o.w = 0.6 + Math.random() * 1.1;
      o.spd = 0.6 + Math.random() * 0.9;
      return o;
    }
    function step(dt, time) {
      /* fade trails without touching what's behind the canvas */
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,' + (0.055 + 0.01 * intensity) + ')';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      var tz = time * DRIFT;
      var k = Math.min(2.2, dt / 16.7);
      for (var i = 0; i < parts.length; i++) {
        var o = parts[i];
        var n = noise(o.x * SCALE + tz, o.y * SCALE - tz * 0.7);
        var ang = n * Math.PI * 4 + tz * 3;
        var vx = Math.cos(ang), vy = Math.sin(ang);
        if (pointer.active) {
          var dx = o.x - pointer.x, dy = o.y - pointer.y;
          var d2 = dx * dx + dy * dy, R = 190;
          if (d2 < R * R) {
            var d = Math.sqrt(d2) || 1, f = (1 - d / R) * 2.2;
            /* swirl around the cursor + gentle push away */
            vx += (-dy / d) * f + (dx / d) * f * 0.35;
            vy += (dx / d) * f + (dy / d) * f * 0.35;
          }
        }
        o.px = o.x; o.py = o.y;
        o.x += vx * SPEED * o.spd * k;
        o.y += vy * SPEED * o.spd * k;
        o.age += k;
        var fadeIn = Math.min(1, o.age / 18), fadeOut = Math.min(1, (o.life - o.age) / 30);
        var alpha = o.a * Math.min(fadeIn, fadeOut);
        if (o.age >= o.life || o.x < -8 || o.x > W + 8 || o.y < -8 || o.y > H + 8) { spawn(o); continue; }
        ctx.strokeStyle = 'rgba(' + o.r + ',' + o.g + ',' + o.b + ',' + alpha.toFixed(3) + ')';
        ctx.lineWidth = o.w;
        ctx.beginPath(); ctx.moveTo(o.px, o.py); ctx.lineTo(o.x, o.y); ctx.stroke();
      }
      ctx.globalCompositeOperation = 'source-over';
    }
    function frame(now) {
      raf = 0;
      if (!running) return;
      var dt = now - last; last = now;
      if (dt > 250) dt = 16.7; /* tab restore / long stall: don't jump */
      step(dt, now - t0);
      /* self-throttle: sustained slow frames → fewer particles, once */
      if (dt > 30) { if (++slowFrames > 45 && budget > 0.5) { budget = 0.5; seed(); slowFrames = -1e9; } }
      else if (slowFrames > 0) slowFrames--;
      raf = requestAnimationFrame(frame);
    }
    function start() {
      if (running || reduce) return;
      running = true; last = performance.now();
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      running = false;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
    }
    function sync() {
      if (visible && pageVisible) start(); else stop();
    }

    resize();
    canvas.classList.add('is-on');

    if (reduce) {
      /* one still frame: pre-roll the field so it reads as finished art */
      for (var f = 0; f < 140; f++) step(16.7, f * 16.7);
      return;
    }

    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(root);
    else window.addEventListener('resize', resize);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting; sync();
      }, { rootMargin: '120px 0px' }).observe(root);
    } else { visible = true; sync(); }
    document.addEventListener('visibilitychange', function () {
      pageVisible = document.visibilityState === 'visible'; sync();
    });

    /* pointer swirl — hover-capable devices only */
    if (window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      root.addEventListener('pointermove', function (e) {
        var r = root.getBoundingClientRect();
        pointer.x = e.clientX - r.left; pointer.y = e.clientY - r.top; pointer.active = true;
      }, { passive: true });
      root.addEventListener('pointerleave', function () { pointer.active = false; });
    }
  }

  /* ======================================================================
     Countdown
     ====================================================================== */
  function initCountdown(root) {
    var el = root.querySelector('[data-pk30-count]');
    if (!el) return;
    var target = parseInt(el.dataset.target, 10) * 1000;
    var out = el.querySelector('[data-pk30-count-time]');
    if (!target || !out) return;
    var label = el.querySelector('.pk30__pill-label');
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var ms = target - Date.now();
      if (ms <= 0) {
        if (label) label.textContent = '';
        out.textContent = el.dataset.done || 'Out now';
        el.classList.add('pk30__pill--done');
        return false;
      }
      var s = Math.floor(ms / 1000);
      var d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
      out.textContent = (d > 0 ? d + 'd ' : '') + pad(h) + 'h ' + pad(m) + 'm' + (d === 0 ? ' ' + pad(sec) + 's' : '');
      return true;
    }
    if (tick()) {
      var id = setInterval(function () { if (!tick()) clearInterval(id); }, 1000);
    }
  }

  /* ======================================================================
     Peek dialog (catalog cards)
     ====================================================================== */
  function initPeek(root) {
    var dialog = root.querySelector('[data-pk30-dialog]');
    var body = root.querySelector('[data-pk30-dialog-body]');
    if (!dialog || !body || typeof dialog.showModal !== 'function') return;
    var lastFocus = null;
    function open(btn) {
      var tpl = btn.parentElement && btn.parentElement.querySelector('template[data-pk30-detail]');
      if (!tpl) return;
      body.innerHTML = '';
      body.appendChild(tpl.content.cloneNode(true));
      lastFocus = btn;
      dialog.showModal();
      document.documentElement.classList.add('pk30-dialog-open');
    }
    function close() {
      if (dialog.open) dialog.close();
    }
    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-pk30-peek]') : null;
      if (btn) { e.preventDefault(); open(btn); return; }
      if (e.target.closest && e.target.closest('[data-pk30-close]')) { close(); }
    });
    /* backdrop click closes: the dialog element itself is the click target there */
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });
    /* Escape: native <dialog> handles it via cancel, but be explicit for older engines */
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && dialog.open) close(); });
    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('pk30-dialog-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    });
  }

  /* ======================================================================
     Shelf: scroller controls (GSAP-independent) + motion
     ====================================================================== */
  function initShelf(root) {
    var viewport = root.querySelector('[data-pk30-viewport]');
    var track = root.querySelector('[data-pk30-track]');
    var items = track ? [].slice.call(track.querySelectorAll('[data-pk30-item]')) : [];
    if (!viewport || !track || !items.length) return;

    var prev = root.querySelector('[data-pk30-prev]');
    var next = root.querySelector('[data-pk30-next]');
    function updateOverflow() {
      root.classList.toggle('pk30--overflow', track.scrollWidth > track.clientWidth + 4);
      updateArrows();
    }
    function updateArrows() {
      var max = track.scrollWidth - track.clientWidth;
      var atStart = track.scrollLeft <= 2, atEnd = track.scrollLeft >= max - 2;
      root.classList.toggle('pk30--fade-l', !atStart);
      root.classList.toggle('pk30--fade-r', !atEnd);
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }
    function nudge(dir) { track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' }); }
    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });
    var scrollRaf = 0;
    track.addEventListener('scroll', function () {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () { scrollRaf = 0; updateArrows(); });
    }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(updateOverflow).observe(track);
    updateOverflow();

    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var design = !!(window.Shopify && window.Shopify.designMode);
    var hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
    if (!hasGsap || reduce) return;
    try { gsap.registerPlugin(ScrollTrigger); } catch (e) { return; }

    if (!design && document.visibilityState === 'visible') {
      var head = root.querySelector('.pk30__head');
      var revealed = false;
      var targets = head ? [head].concat(items) : items;
      function settle() {
        if (revealed) return;
        revealed = true;
        gsap.set(targets, { clearProps: 'transform,opacity,visibility' });
      }
      gsap.set(items, { autoAlpha: 0, y: 28, scale: 0.96 });
      if (head) gsap.set(head, { autoAlpha: 0, y: 18 });
      var tl = gsap.timeline({ paused: true, onComplete: settle });
      if (head) tl.to(head, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out' }, 0);
      tl.to(items, { autoAlpha: 1, y: 0, scale: 1, duration: 0.65, ease: 'back.out(1.4)', stagger: { each: 0.05, from: 'start' } }, 0.12);
      var trigger = ScrollTrigger.create({
        trigger: root, start: 'clamp(top 85%)', once: true,
        onEnter: function () { if (!revealed) tl.play(); }
      });
      function forceReveal() {
        if (revealed) return;
        try { trigger.kill(); tl.kill(); } catch (e) { /* settle regardless */ }
        settle();
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.disconnect();
            setTimeout(function () {
              if (revealed || tl.isActive() || tl.progress() > 0) return;
              forceReveal();
            }, 500);
          });
        }, { threshold: 0.05 });
        io.observe(viewport);
      }
      setTimeout(function () {
        if (!revealed && !tl.isActive() && tl.progress() === 0) {
          var r = root.getBoundingClientRect();
          if (r.top < innerHeight && r.bottom > 0) forceReveal();
        }
      }, 3500);
      window.addEventListener('pageshow', function (e) { if (e.persisted) forceReveal(); });
    }

    var mm = gsap.matchMedia();
    mm.add('(pointer: fine)', function () {
      var offs = items.map(function (item) {
        var card = item.querySelector('.pk30__link');
        if (!card) return null;
        var liftY = gsap.quickTo(card, 'y', { duration: 0.35, ease: 'power3.out' });
        var liftS = gsap.quickTo(card, 'scale', { duration: 0.35, ease: 'power3.out' });
        function enter() { liftY(-6); liftS(1.028); }
        function leave() { liftY(0); liftS(1); }
        function down() { liftS(0.975); }
        function up() { liftS(1.028); }
        card.addEventListener('pointerenter', enter);
        card.addEventListener('pointerleave', leave);
        card.addEventListener('pointerdown', down);
        card.addEventListener('pointerup', up);
        return function () {
          card.removeEventListener('pointerenter', enter);
          card.removeEventListener('pointerleave', leave);
          card.removeEventListener('pointerdown', down);
          card.removeEventListener('pointerup', up);
          gsap.set(card, { clearProps: 'transform' });
        };
      });
      return function () { offs.forEach(function (off) { if (off) off(); }); };
    });
  }

  /* ======================================================================
     Mascot: idles, blinks, hops to random spots above the shelf, flees the
     cursor, sparks when tapped. GSAP required (loaded by theme.liquid);
     without it, or under reduced motion, it just sits top-right.
     ====================================================================== */
  function initPika(root) {
    var el = root.querySelector('[data-pk30-pika]');
    if (!el) return;
    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hasGsap = typeof gsap !== 'undefined';
    var svg = el.querySelector('svg');
    var W = 0, H = 0, size = 64, x = 0, y = 0, facing = 1, busy = false, alive = false;
    var timer = 0, blinkTimer = 0, visible = false, pageVisible = document.visibilityState === 'visible';

    function bounds() {
      var r = root.getBoundingClientRect();
      W = r.width; size = el.offsetWidth || 64;
      /* stay above the cards: header band only */
      var track = root.querySelector('[data-pk30-track]');
      var top = track ? track.getBoundingClientRect().top - r.top : r.height * 0.5;
      H = Math.max(size + 8, top - size - 6);
    }
    function place(nx, ny, flip) {
      x = nx; y = ny;
      if (hasGsap) gsap.set(el, { x: x, y: y });
      else { el.style.transform = 'translate(' + x + 'px,' + y + 'px)'; }
      if (flip !== undefined && svg) {
        facing = flip;
        if (hasGsap) gsap.set(svg, { scaleX: facing }); else svg.style.transform = 'scaleX(' + facing + ')';
      }
    }
    function randomSpot(minDist) {
      var nx, ny, tries = 0;
      do {
        /* weight toward the right half where the header has air */
        var t = Math.random();
        nx = (t < 0.35 ? Math.random() * 0.5 : 0.5 + Math.random() * 0.5) * (W - size);
        ny = Math.random() * H;
        tries++;
      } while (Math.hypot(nx - x, ny - y) < (minDist || 60) && tries < 12);
      return { x: nx, y: ny };
    }

    bounds();
    place(Math.max(0, W - size - 8), Math.max(0, Math.min(H, 4)), -1);
    if ('ResizeObserver' in window) new ResizeObserver(function () {
      bounds(); place(Math.min(x, W - size), Math.min(y, H));
    }).observe(root);

    if (!hasGsap || reduce) return;

    var ears = el.querySelectorAll('.pk30__pika-ear');
    var eyes = el.querySelector('.pk30__pika-eyes');
    var tail = el.querySelector('.pk30__pika-tail');
    var body = el.querySelector('.pk30__pika-svg');
    var shadow = el.querySelector('.pk30__pika-shadow');

    /* idle: breathing + tail sway (paused when not alive) */
    var idle = gsap.timeline({ repeat: -1, yoyo: true, paused: true })
      .to(body, { scaleY: 1.03, duration: 0.9, ease: 'sine.inOut', transformOrigin: '50% 100%' }, 0)
      .to(tail, { rotation: 8, duration: 0.9, ease: 'sine.inOut' }, 0);

    function blink() {
      if (!alive) return;
      gsap.to(eyes, { scaleY: 0.1, duration: 0.07, yoyo: true, repeat: 1, ease: 'power1.inOut' });
      blinkTimer = setTimeout(blink, 2200 + Math.random() * 3800);
    }
    function wiggle() {
      gsap.to(ears, { rotation: function (i) { return i ? 10 : -10; }, duration: 0.12, yoyo: true, repeat: 3, ease: 'sine.inOut' });
    }

    function hop(target, opts) {
      if (busy) return;
      busy = true;
      opts = opts || {};
      var dx = target.x - x, dy = target.y - y;
      var dist = Math.hypot(dx, dy);
      var dur = Math.min(0.9, 0.38 + dist / 520);
      var height = Math.min(110, 34 + dist * 0.28);
      var dir = dx < -2 ? -1 : dx > 2 ? 1 : facing;
      var tl = gsap.timeline({ onComplete: function () { busy = false; if (opts.then) opts.then(); } });
      /* face the target, crouch */
      tl.to(body, { scaleX: dir, duration: 0 }, 0);
      tl.to(body, { scaleY: 0.82, duration: 0.11, ease: 'power2.out', transformOrigin: '50% 100%' }, 0);
      tl.to(shadow, { scaleX: 1.15, opacity: 0.9, duration: 0.11 }, 0);
      /* launch: x linear, y parabola via two tweens */
      tl.to(body, { scaleY: 1.12, scaleX: dir * 0.92, duration: 0.14, ease: 'power2.out' }, 0.11);
      tl.to(el, { x: target.x, duration: dur, ease: 'none' }, 0.11);
      tl.to(el, { y: Math.min(target.y, y) - height, duration: dur * 0.5, ease: 'power2.out' }, 0.11);
      tl.to(el, { y: target.y, duration: dur * 0.5, ease: 'power2.in' }, 0.11 + dur * 0.5);
      tl.to(shadow, { scaleX: 0.55, opacity: 0.35, duration: dur * 0.5, ease: 'power2.out' }, 0.11);
      tl.to(shadow, { scaleX: 1, opacity: 0.9, duration: dur * 0.5, ease: 'power2.in' }, 0.11 + dur * 0.5);
      tl.to(ears, { rotation: function (i) { return i ? -14 : 14; }, duration: dur * 0.5, ease: 'power1.out' }, 0.11);
      tl.to(ears, { rotation: 0, duration: dur * 0.5, ease: 'power1.in' }, 0.11 + dur * 0.5);
      /* land: squash, settle */
      tl.to(body, { scaleY: 0.8, scaleX: dir * 1.14, duration: 0.09, ease: 'power2.out' }, 0.11 + dur);
      tl.to(body, { scaleY: 1, scaleX: dir, duration: 0.32, ease: 'elastic.out(1, 0.45)' }, 0.2 + dur);
      tl.add(function () { x = target.x; y = target.y; facing = dir; }, 0.11 + dur);
    }

    function schedule() {
      clearTimeout(timer);
      if (!alive) return;
      timer = setTimeout(function () {
        if (!alive) return;
        var r = Math.random();
        if (r < 0.18) { wiggle(); schedule(); return; }           /* just look around */
        var dbl = r > 0.8;
        hop(randomSpot(90), { then: function () {
          if (dbl && alive) { setTimeout(function () { hop(randomSpot(70), { then: schedule }); }, 140); }
          else schedule();
        } });
      }, 1400 + Math.random() * 3200);
    }

    function wake() {
      if (alive) return;
      alive = true; idle.play(); blink(); schedule();
    }
    function sleep() {
      alive = false; idle.pause(); clearTimeout(timer); clearTimeout(blinkTimer);
    }
    function sync() { if (visible && pageVisible) wake(); else sleep(); }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; sync(); }, { threshold: 0.05 }).observe(root);
    } else { visible = true; sync(); }
    document.addEventListener('visibilitychange', function () { pageVisible = document.visibilityState === 'visible'; sync(); });

    /* flee the cursor (desktop) */
    if (window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var lastFlee = 0;
      root.addEventListener('pointermove', function (e) {
        if (!alive || busy || Date.now() - lastFlee < 900) return;
        var r = root.getBoundingClientRect();
        var px = e.clientX - r.left, py = e.clientY - r.top;
        var cx = x + size / 2, cy = y + size / 2;
        if (Math.hypot(px - cx, py - cy) < 80) {
          lastFlee = Date.now();
          var away = px < cx ? 1 : -1;
          var nx = Math.max(0, Math.min(W - size, cx + away * (140 + Math.random() * 160) - size / 2));
          var ny = Math.max(0, Math.min(H, y + (Math.random() - 0.5) * 80));
          clearTimeout(timer);
          hop({ x: nx, y: ny }, { then: schedule });
        }
      }, { passive: true });
    }

    /* tap: spark burst + happy wiggle + hop */
    el.addEventListener('click', function () {
      wiggle();
      for (var i = 0; i < 8; i++) {
        var s = document.createElement('span');
        s.className = 'pk30__spark';
        root.appendChild(s);
        var a = (Math.PI * 2 * i) / 8 + Math.random() * 0.5;
        gsap.set(s, { x: x + size / 2, y: y + size / 2, scale: 0.6 + Math.random() * 0.8 });
        gsap.to(s, { x: '+=' + Math.cos(a) * (40 + Math.random() * 40), y: '+=' + (Math.sin(a) * (40 + Math.random() * 40) - 20),
          opacity: 0, scale: 0.2, duration: 0.55 + Math.random() * 0.3, ease: 'power2.out',
          onComplete: function (node) { node.remove(); }, onCompleteParams: [s] });
      }
      if (!busy) { clearTimeout(timer); hop(randomSpot(120), { then: schedule }); }
    });
  }

  function init(root) {
    if (root.dataset.pk30Ready) return;
    root.dataset.pk30Ready = '1';
    try { initFlow(root); } catch (e) { /* decorative only */ }
    try { initPika(root); } catch (e) { /* decorative only */ }
    initCountdown(root);
    initPeek(root);
    initShelf(root);
  }
  function initAll(scope) {
    [].slice.call((scope || document).querySelectorAll('[data-pk30]')).forEach(init);
  }
  if (document.readyState === 'complete') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-pk30]')) initAll(e.target);
  });
})();
